import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { View, ScrollView, Platform, ActivityIndicator, TextInput, Linking } from 'react-native';
import { SafeAreaView, useSafeAreaInsets } from 'react-native-safe-area-context';
import { MaterialIcons } from '@expo/vector-icons';
import { useRouter, useLocalSearchParams } from 'expo-router';

import { Card, CardContent } from '@/components/ui/card';
import { Text } from '@/components/ui/text';
import { Button } from '@/components/ui/button';

import { apiRequest } from '../../services/apiClient';
import { billingAPI } from '../../services/api';
import { useTranslation } from '../../hooks/useTranslation';
import { useAuth } from '../../context/AuthContext';
import type { SchoolPlanPreview } from '@/types';
import { trackAuthEvent } from '../../services/analytics';
import { ROUTES } from '../../navigation/routes';
import {
  createCheckoutError,
  getCheckoutErrorDetails,
  showCheckoutFailureDialog,
} from '../../utils/checkoutFailures';
import { brand } from '../../design-system/theme/tokens';
import {
  planPreviewErrorCode,
  planPreviewErrorKind,
  planPreviewErrorMessageKey,
  schoolSupportMailto,
  type B2BPlanErrorCode,
} from './planPreviewError';

const CHECKOUT_SURFACE = 'b2b_plan_selection';

const COLORS = {
  primary: brand.primary,
  secondary: '#F59E0B',
  success: '#10B981',
  error: '#DC2626',
  background: '#FFFFFF',
  surface: '#F9FAFB',
};

const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));
const isActiveBillingStatus = (status?: string | null) => ['active', 'trialing'].includes((status || '').toLowerCase());

export default function B2BPlanSelectionScreenEnhanced() {
  const router = useRouter();
  const { t } = useTranslation();
  const insets = useSafeAreaInsets();
  const { user, refreshAuth, logout } = useAuth();
  const params = useLocalSearchParams();

  const normalizedUserType = (user?.user_type || '').toLowerCase();
  const isB2BAdmin = normalizedUserType === 'b2b2c_admin';
  const schoolId = user?.school_id ?? null;

  const [plan, setPlan] = useState<SchoolPlanPreview | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Why the error state is shown, for analytics (the message itself is translated text).
  const [errorCode, setErrorCode] = useState<B2BPlanErrorCode | null>(null);
  useEffect(() => {
    if (!error) return;
    trackAuthEvent('b2b_plan_selection_error_shown', { error_code: errorCode ?? 'unknown' });
  }, [error, errorCode]);
  const trackErrorAction = (action: 'retry' | 'contact_support' | 'sign_out') =>
    trackAuthEvent('b2b_plan_selection_error_action', { action, error_code: errorCode ?? 'unknown' });
  const [seatTarget, setSeatTarget] = useState('10');
  const [isProcessing, setIsProcessing] = useState(false);
  const checkoutStatus = Array.isArray(params.status) ? params.status[0] : params.status;
  const [isRedirecting, setIsRedirecting] = useState(checkoutStatus === 'success');

  useEffect(() => {
    trackAuthEvent('b2b_plan_selection_viewed');
  }, []);

  const waitForBillingActivation = useCallback(async () => {
    let latestStatus: string | null | undefined = null;
    for (let attempt = 0; attempt < 6; attempt += 1) {
      try {
        await billingAPI.refresh({ force: true });
        const latest = await billingAPI.status({ force: true });
        latestStatus = latest?.subscription_status;
        if (isActiveBillingStatus(latestStatus)) {
          return true;
        }
      } catch (activationError) {
        console.warn('B2B billing activation refresh failed:', activationError);
      }
      await delay(attempt < 2 ? 1000 : 2000);
    }
    return false;
  }, []);

  // Handle successful payment return from Stripe
  useEffect(() => {
    const handlePaymentSuccess = async () => {
      if (checkoutStatus === 'success') {
        setIsRedirecting(true);
        try {
          const activated = await waitForBillingActivation();
          await refreshAuth({ forceBillingRefresh: true, forceUserRefresh: true });
          await delay(500);

          if (!activated) {
            setError(t('b2bPlanSelection.errors.activationPending'));
            setErrorCode('activation_pending');
            setIsRedirecting(false);
            return;
          }

          if (user?.onboarding_completed) {
            router.replace(ROUTES.GATED_POST_PAYMENT_ONBOARDING as any);
          } else {
            router.replace(ROUTES.GATED_ONBOARDING as any);
          }
        } catch (paymentError) {
          console.error('Error handling payment success:', paymentError);
          setError(t('b2bPlanSelection.errors.activationPending'));
          setErrorCode('activation_pending');
          setIsRedirecting(false);
        }
      }
    };

    handlePaymentSuccess();
  }, [checkoutStatus, refreshAuth, router, t, user?.onboarding_completed, waitForBillingActivation]);

  const fetchPlanDetails = useCallback(async (
    targetSchoolId: number,
    options?: { seatTarget?: number; hydrateForm?: boolean }
  ) => {
    const hydrateForm = options?.hydrateForm !== false;
    try {
      if (hydrateForm) {
        setLoading(true);
      }
      setError(null);
      const planDetails = await apiRequest<SchoolPlanPreview>({
        endpoint: `/billing/schools/${targetSchoolId}/plan-preview`,
        method: 'GET',
        params: {
          seat_target: options?.seatTarget,
        },
      });
      setPlan(planDetails);
      if (hydrateForm) {
        setSeatTarget(String(planDetails.requested_seat_target ?? planDetails.effective_seat_target ?? planDetails.base_seats ?? 10));
      }
    } catch (fetchError) {
      const kind = planPreviewErrorKind(fetchError);
      setError(t(planPreviewErrorMessageKey(kind)));
      setErrorCode(planPreviewErrorCode(kind));
    } finally {
      if (hydrateForm) {
        setLoading(false);
      }
    }
  }, [t]);

  useEffect(() => {
    if (!schoolId) {
      setLoading(false);
      setPlan(null);
      setError(t('b2bPlanSelection.errors.noSchool'));
      setErrorCode('no_school');
      return;
    }
    fetchPlanDetails(schoolId, { hydrateForm: true });
  }, [schoolId, t, fetchPlanDetails]);

  useEffect(() => {
    if (!schoolId || loading) return;
    const parsed = parseInt(seatTarget, 10);
    if (Number.isNaN(parsed) || parsed <= 0) return;
    if (
      plan &&
      parsed === (plan.requested_seat_target ?? plan.effective_seat_target)
    ) {
      return;
    }
    const timer = setTimeout(() => {
      fetchPlanDetails(schoolId, {
        seatTarget: parsed,
        hydrateForm: false,
      });
    }, 350);
    return () => clearTimeout(timer);
  }, [fetchPlanDetails, loading, plan, schoolId, seatTarget]);

  const calculateTotal = useCallback((seatValue: string, data: SchoolPlanPreview | null) => {
    if (!data) return 0;
    const seats = parseInt(seatValue, 10);
    if (isNaN(seats)) return 0;

    const over = Math.max(seats - data.base_seats, 0);
    const pkgSize = data.addon_package_size;
    const packages = pkgSize > 0 ? Math.ceil(over / pkgSize) : 0;
    return data.base_price_cents + packages * data.addon_price_cents;
  }, []);

  const localCalculatedTotal = useMemo(
    () => calculateTotal(seatTarget, plan),
    [calculateTotal, plan, seatTarget]
  );
  const calculatedTotal = plan?.total_cents ?? localCalculatedTotal;

  const handleSeatTargetChange = (value: string) => {
    const sanitized = value.replace(/[^0-9]/g, '');
    setSeatTarget(sanitized);
  };

  const handleCheckout = async () => {
    if (!schoolId) {
      setError(t('b2bPlanSelection.errors.noSchool'));
      setErrorCode('no_school');
      return;
    }
    try {
      setIsProcessing(true);
      trackAuthEvent('b2b_checkout_initiated', {
        seats: parseInt(seatTarget, 10),
        price_cents: calculatedTotal,
        amount: calculatedTotal / 100,
        amount_cents: calculatedTotal,
        currency: plan?.currency,
      });
      const response = await apiRequest({
        endpoint: `/billing/schools/${schoolId}/plan-selection-checkout`,
        method: 'POST',
        data: {
          seat_target: parseInt(seatTarget, 10),
          plan_tier: plan?.plan_tier,
        },
      });

      if (!response.checkout_url) {
        throw createCheckoutError('missing_checkout_url');
      }
      trackAuthEvent('checkout_session_created', {
        context: 'b2b',
        price_cents: calculatedTotal,
        amount: calculatedTotal / 100,
        amount_cents: calculatedTotal,
        currency: plan?.currency,
      });
      if (Platform.OS === 'web') {
        window.location.href = response.checkout_url;
      } else {
        // In a native app, open Stripe in the default browser
        await Linking.openURL(response.checkout_url);
      }
    } catch (e: any) {
      const diagnostics = getCheckoutErrorDetails(e);
      trackAuthEvent('b2b_checkout_failed', {
        seats: parseInt(seatTarget, 10),
        error_code: diagnostics.errorCode,
        error_type: diagnostics.errorType,
        price_cents: calculatedTotal,
        amount: calculatedTotal / 100,
        amount_cents: calculatedTotal,
        currency: plan?.currency,
      });
      setIsProcessing(false);
      await showCheckoutFailureDialog({
        error: e,
        surface: CHECKOUT_SURFACE,
        checkoutKey: `${schoolId}:${seatTarget}`,
        properties: {
          seats: parseInt(seatTarget, 10),
          price_cents: calculatedTotal,
          amount: calculatedTotal / 100,
          amount_cents: calculatedTotal,
          currency: plan?.currency,
        },
        retry: handleCheckout,
        t,
      });
    } finally {
      setIsProcessing(false);
    }
  };

  if (!isB2BAdmin) {
    return (
      <SafeAreaView className="flex-1 bg-surface items-center justify-center px-6">
        <MaterialIcons name="lock" size={40} color={COLORS.primary} />
        <Text className="mt-4 text-center text-muted-foreground">
          {t('b2bPlanSelection.errors.unauthorized')}
        </Text>
      </SafeAreaView>
    );
  }

  if (isRedirecting) {
    return (
      <View className="flex-1 justify-center items-center">
        <ActivityIndicator size="large" color={COLORS.primary} />
        <Text className="mt-4 text-lg text-muted-foreground">{t('common.loading')}</Text>
      </View>
    );
  }

  if (loading) {
    return <View className="flex-1 justify-center items-center"><ActivityIndicator size="large" color={COLORS.primary} /></View>;
  }

  if (error) {
    // Never a dead end: a school with no billing plan can't be fixed by
    // retrying, so the admin can also reach support or sign out.
    return (
      <View className="flex-1 justify-center items-center px-6" testID="b2b-plan-error">
        <Text className="text-red-500 text-center mb-4" testID="b2b-plan-error-message">{error}</Text>
        <View className="w-full max-w-sm gap-3">
          <Button
            testID="b2b-plan-error-retry"
            onPress={async () => {
              trackErrorAction('retry');
              setError(null);
              if (schoolId) {
                await fetchPlanDetails(schoolId, { hydrateForm: true });
              }
              await refreshAuth({ forceBillingRefresh: true, forceUserRefresh: true });
            }}
          >
            <Text>{t('b2bPlanSelection.errorActions.retry')}</Text>
          </Button>
          <Button
            variant="outline"
            testID="b2b-plan-error-contact"
            onPress={() => {
              trackErrorAction('contact_support');
              void Linking.openURL(schoolSupportMailto());
            }}
          >
            <Text>{t('b2bPlanSelection.errorActions.contactSupport')}</Text>
          </Button>
          <Button
            variant="ghost"
            testID="b2b-plan-error-signout"
            onPress={() => {
              trackErrorAction('sign_out');
              void logout();
            }}
          >
            <Text>{t('b2bPlanSelection.errorActions.signOut')}</Text>
          </Button>
        </View>
      </View>
    );
  }

  return (
    <SafeAreaView className="flex-1 bg-surface">
      <ScrollView contentContainerStyle={{ paddingBottom: Math.max(insets.bottom + 16, 32) }}>
        <View className="w-full max-w-4xl mx-auto px-4 md:px-6 lg:px-8 py-8">
          <Text className="text-3xl font-bold text-center mb-2">{t('b2bPlanSelection.title')}</Text>
          <Text className="text-lg text-center text-muted-foreground mb-8">{t('b2bPlanSelection.subtitle')}</Text>

          <Card>
            <CardContent className="p-6">
              <View className="flex-row justify-between items-center mb-6">
                <Text className="text-xl font-semibold">{t('b2bPlanSelection.yourPlan')}</Text>
                <View style={{ backgroundColor: COLORS.primary, paddingHorizontal: 12, paddingVertical: 4, borderRadius: 999 }}>
                  <Text className="text-white font-bold">{(user as any)?.school?.name}</Text>
                </View>
              </View>

              <View className="mb-6">
                <Text className="text-base font-semibold mb-2">{t('b2bPlanSelection.seats')}</Text>
                <TextInput
                  value={seatTarget}
                  onChangeText={handleSeatTargetChange}
                  keyboardType="number-pad"
                  className="bg-gray-50 border border-gray-200 rounded-lg px-4 py-3 text-base text-foreground"
                />
                <Text className="text-sm text-muted-foreground mt-2">
                  {t('b2bPlanSelection.seatHelper', { base: plan?.base_seats ?? 0, bundle: plan?.addon_package_size ?? 0 })}
                </Text>
              </View>

              <View className="border-t border-gray-200 pt-6">
                <View className="flex-row justify-between items-center mb-4">
                  <Text className="text-lg font-semibold">{t('b2bPlanSelection.total')}</Text>
                  <Text className="text-2xl font-bold">{new Intl.NumberFormat('en-US', { style: 'currency', currency: plan?.currency || 'USD' }).format(calculatedTotal / 100)}</Text>
                </View>
                <Text className="text-xs text-muted-foreground mb-4">{t('b2bPlanSelection.priceDisclaimer')}</Text>

                {/* Money-back Guarantee */}
                <View className="flex-row items-center justify-center bg-green-50 border border-green-200 rounded-lg px-3 py-2 mb-4">
                  <MaterialIcons name="verified" size={16} color="#059669" style={{ marginRight: 6 }} />
                  <Text className="text-xs font-semibold text-green-700">
                    {t('plans.moneyBackGuarantee')}
                  </Text>
                </View>

                <Button onPress={handleCheckout} disabled={isProcessing}>
                  {isProcessing ? (
                    <ActivityIndicator color="white" />
                  ) : (
                    <Text>{t('b2bPlanSelection.checkout')}</Text>
                  )}
                </Button>
              </View>
            </CardContent>
          </Card>
        </View>
      </ScrollView>
    </SafeAreaView>
  );
}
